import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Nav, Form, Button, Alert } from 'react-bootstrap';
import axios from 'axios';
import Icon from './Icon';
import { useViewMode } from '../contexts/ViewModeContext';
import { useAuth } from '../contexts/AuthContext';

function Sidebar() {
  const location = useLocation();
  const { viewMode, setViewMode, isAdvanced } = useViewMode();
  const { user, updateUser, isDataReadOnly } = useAuth();
  const [userRole, setUserRole] = useState(null);
  const [dataCompleteAt, setDataCompleteAt] = useState(null);
  const [counts, setCounts] = useState({});
  const [completingData, setCompletingData] = useState(false);
  const [completeError, setCompleteError] = useState('');
  const [completeSuccess, setCompleteSuccess] = useState('');

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await axios.get('/api/user-management/current');
        setUserRole(response.data.role);
        setDataCompleteAt(response.data.data_complete_at || null);
      } catch (error) {
        // Silently handle errors
      }
    };
    fetchCurrentUser();
  }, [user?.dataCompleteAt]);

  useEffect(() => {
    if (user?.dataCompleteAt) {
      setDataCompleteAt(user.dataCompleteAt);
    }
  }, [user?.dataCompleteAt]);

  const countEndpoints = useMemo(() => {
    const endpoints = {
      depots: '/api/depots',
      customers: '/api/customers',
      'customer-sites': '/api/customer-sites',
      'customer-contacts': '/api/customer-contacts',
      suppliers: '/api/suppliers',
      staff: '/api/staff',
      'hire-rates': '/api/hire-rates',
      'hire-category-groups': '/api/hire-category-groups',
      'hire-categories': '/api/hire-categories',
      'hire-stock': '/api/hire-stock',
      'hire-stock-bulk': '/api/hire-stock-bulk',
      'sales-categories': '/api/sales-categories',
      'sales-items': '/api/sales-items',
      'fleet-reminders': '/api/fleet-reminders',
      'inspection-tests': '/api/inspection-tests',
      inclusions: '/api/inclusions',
      'nominal-codes': '/api/nominal-codes',
      'validation-failures': '/api/validation-failures',
    };
    if (isAdvanced) {
      Object.assign(endpoints, {
        'pricing-agreements': '/api/pricing-agreements',
        'pricing-agreement-customer-sites': '/api/pricing-agreement-customer-sites',
        'pricing-agreement-hire-rates': '/api/pricing-agreement-hire-rates',
        'pricing-agreement-sales-prices': '/api/pricing-agreement-sales-prices',
        'pricing-agreement-transport-rates': '/api/pricing-agreement-transport-rates',
        'sales-items-individual': '/api/sales-items-individual',
        'customer-equipment': '/api/customer-equipment',
        'fleet-service-history': '/api/fleet-service-history',
        'meter-readings': '/api/meter-readings',
        'live-contracts': '/api/live-contracts',
      });
    }
    return endpoints;
  }, [isAdvanced]);

  useEffect(() => {
    const fetchCounts = async () => {
      const newCounts = {};

      await Promise.all(
        Object.entries(countEndpoints).map(async ([key, endpoint]) => {
          try {
            const response = await axios.get(`${endpoint}?limit=1`);
            if (response.data?.pagination?.total !== undefined) {
              newCounts[key] = response.data.pagination.total;
            } else if (Array.isArray(response.data)) {
              const fullResponse = await axios.get(endpoint);
              newCounts[key] = Array.isArray(fullResponse.data) ? fullResponse.data.length : 0;
            }
          } catch {
            // Silently ignore errors
          }
        })
      );

      setCounts(newCounts);
    };

    fetchCounts();
  }, [countEndpoints]);

  const renderCount = (countKey) => {
    if (!countKey || counts[countKey] === undefined) return null;
    return (
      <span
        style={{
          fontSize: '0.65rem',
          marginLeft: '0.25rem',
          opacity: 0.7,
        }}
      >
        ({counts[countKey]})
      </span>
    );
  };

  const menuItems = useMemo(() => {
    const assetManagementItems = [
      {
        path: '/hire-rates',
        label: 'Hire Rates',
        icon: 'currencyDollar',
        countKey: 'hire-rates',
      },
      {
        path: '/hire-category-groups',
        label: 'Hire Category Groups',
        icon: 'collection',
        countKey: 'hire-category-groups',
      },
      {
        path: '/hire-categories',
        label: 'Hire Categories',
        icon: 'collection',
        countKey: 'hire-categories',
      },
      { path: '/hire-stock', label: 'Hire Stock', icon: 'cube', countKey: 'hire-stock' },
      {
        path: '/hire-stock-bulk',
        label: 'Hire Stock Bulk',
        icon: 'cube',
        countKey: 'hire-stock-bulk',
      },
      {
        path: '/sales-categories',
        label: 'Sales Categories',
        icon: 'collection',
        countKey: 'sales-categories',
      },
      { path: '/sales-items', label: 'Sales Items', icon: 'cube', countKey: 'sales-items' },
    ];

    if (isAdvanced) {
      assetManagementItems.push(
        {
          path: '/sales-items-individual',
          label: 'Sales Items Individual',
          icon: 'cube',
          countKey: 'sales-items-individual',
        },
        {
          path: '/customer-equipment',
          label: 'Customer Equipment',
          icon: 'wrench',
          countKey: 'customer-equipment',
        }
      );
    }

    const items = [
      { path: '/dashboard', label: 'Dashboard', icon: 'dashboard' },
      { path: '/overview', label: 'Overview', icon: 'sitemap' },
      {
        label: 'Wizards',
        isGroup: true,
        items: [{ path: '/fleet-wizard', label: 'Fleet Wizard', icon: 'truck' }],
      },
      {
        label: 'People & Companies',
        isGroup: true,
        items: [
          { path: '/depots', label: 'Depots', icon: 'building', countKey: 'depots' },
          { path: '/customers', label: 'Customers', icon: 'users', countKey: 'customers' },
          {
            path: '/customer-sites',
            label: 'Customer Sites',
            icon: 'building',
            countKey: 'customer-sites',
          },
          {
            path: '/customer-contacts',
            label: 'Customer Contacts',
            icon: 'addressBook',
            countKey: 'customer-contacts',
          },
          { path: '/suppliers', label: 'Suppliers', icon: 'building', countKey: 'suppliers' },
          { path: '/staff', label: 'Staff', icon: 'user', countKey: 'staff' },
        ],
      },
      {
        label: 'Asset Management',
        isGroup: true,
        items: assetManagementItems,
      },
      {
        label: 'Operations',
        isGroup: true,
        items: [
          {
            path: '/fleet-reminders',
            label: 'Fleet Reminders',
            icon: 'clipboard',
            countKey: 'fleet-reminders',
          },
          {
            path: '/inspection-tests',
            label: 'Inspection Tests',
            icon: 'clipboard',
            countKey: 'inspection-tests',
          },
          { path: '/inclusions', label: 'Inclusions', icon: 'clipboard', countKey: 'inclusions' },
        ],
      },
      {
        label: 'Financial',
        isGroup: true,
        items: [
          {
            path: '/nominal-codes',
            label: 'Nominal Codes',
            icon: 'calculator',
            countKey: 'nominal-codes',
          },
        ],
      },
      { path: '/import-export', label: 'Import/Export', icon: 'documentText' },
      {
        path: '/validation-failures',
        label: 'Validation Failures',
        icon: 'clipboard',
        countKey: 'validation-failures',
      },
    ];

    if (isAdvanced) {
      items.splice(5, 0, {
        label: 'Pricing Agreements',
        isGroup: true,
        advancedOnly: true,
        items: [
          {
            path: '/pricing-agreements',
            label: 'Pricing Agreements',
            icon: 'documentText',
            countKey: 'pricing-agreements',
          },
          {
            path: '/pricing-agreement-customer-sites',
            label: 'Customer / Sites',
            icon: 'users',
            countKey: 'pricing-agreement-customer-sites',
          },
          {
            path: '/pricing-agreement-hire-rates',
            label: 'Hire Rates',
            icon: 'currencyDollar',
            countKey: 'pricing-agreement-hire-rates',
          },
          {
            path: '/pricing-agreement-sales-prices',
            label: 'Sales Prices',
            icon: 'cube',
            countKey: 'pricing-agreement-sales-prices',
          },
          {
            path: '/pricing-agreement-transport-rates',
            label: 'Transport Rates',
            icon: 'truck',
            countKey: 'pricing-agreement-transport-rates',
          },
        ],
      });

      items.splice(7, 0, {
        label: 'Readings',
        isGroup: true,
        advancedOnly: true,
        items: [
          {
            path: '/fleet-service-history',
            label: 'Fleet Service History',
            icon: 'clipboard',
            countKey: 'fleet-service-history',
          },
          {
            path: '/meter-readings',
            label: 'Meter Readings',
            icon: 'clipboard',
            countKey: 'meter-readings',
          },
        ],
      });

      items.splice(8, 0, {
        label: 'Contracts',
        isGroup: true,
        advancedOnly: true,
        items: [
          {
            path: '/live-contracts',
            label: 'Live Contracts',
            icon: 'documentText',
            countKey: 'live-contracts',
          },
        ],
      });
    }

    if (userRole === 'admin') {
      items.push({
        label: 'System',
        isGroup: true,
        items: [{ path: '/user-management', label: 'User Management', icon: 'user' }],
      });
    }

    return items;
  }, [isAdvanced, userRole]);

  const isCustomer = userRole && userRole !== 'admin';
  const showDataCompleteButton = isCustomer && !dataCompleteAt && !isDataReadOnly;

  const handleDataComplete = async () => {
    const confirmed = window.confirm(
      'By clicking OK, you confirm that your data has been checked and is complete.\n\n' +
        'Once confirmed, your data will become read-only and you will no longer be able to edit records or import data.\n\n' +
        'Do you want to continue?'
    );
    if (!confirmed) {
      return;
    }

    setCompletingData(true);
    setCompleteError('');
    setCompleteSuccess('');
    try {
      await axios.post('/api/user-management/data-complete');
      const profileRes = await axios.get('/api/auth/profile');
      updateUser(profileRes.data.user);
      setDataCompleteAt(profileRes.data.user.dataCompleteAt || null);
      setCompleteSuccess('Data marked as complete. Your account is now read-only.');
    } catch (error) {
      setCompleteError(error.response?.data?.error || 'Failed to mark data as complete');
    } finally {
      setCompletingData(false);
    }
  };

  const formatCompleteDate = (value) => {
    if (!value) return '';
    return new Date(value).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <div className="sidebar">
      <Nav className="flex-column sidebar-nav-main">
        {menuItems.map((item, index) =>
          item.isGroup ? (
            <div key={index} className="sidebar-section">
              <div className="sidebar-section-title">{item.label}</div>
              {item.items.map((subItem) => (
                <Nav.Link
                  key={subItem.path}
                  as={Link}
                  to={subItem.path}
                  className={`d-flex align-items-center text-decoration-none ${
                    location.pathname === subItem.path ? 'active' : ''
                  }`}
                >
                  <Icon name={subItem.icon} className="me-1 flex-shrink-0" />
                  <span className="small sidebar-link-label">
                    {subItem.label}
                    {renderCount(subItem.countKey)}
                  </span>
                </Nav.Link>
              ))}
            </div>
          ) : (
            <Nav.Link
              key={item.path}
              as={Link}
              to={item.path}
              className={`d-flex align-items-center text-decoration-none ${
                location.pathname === item.path ? 'active' : ''
              }`}
            >
              <Icon name={item.icon} className="me-1 flex-shrink-0" />
              <span className="small sidebar-link-label">
                {item.label}
                {renderCount(item.countKey)}
              </span>
            </Nav.Link>
          )
        )}
      </Nav>

      {isCustomer && (
        <div className="sidebar-data-complete">
          {showDataCompleteButton ? (
            <>
              <Button
                variant="outline-success"
                className="w-100 sidebar-data-complete-btn"
                onClick={handleDataComplete}
                disabled={completingData}
              >
                {completingData ? 'Confirming...' : 'Data Complete'}
              </Button>
              {completeError && (
                <Alert variant="danger" className="py-1 px-2 mt-2 mb-0 small">
                  {completeError}
                </Alert>
              )}
              {completeSuccess && (
                <Alert variant="success" className="py-1 px-2 mt-2 mb-0 small">
                  {completeSuccess}
                </Alert>
              )}
            </>
          ) : (
            dataCompleteAt && (
              <div className="sidebar-data-complete-status small text-muted">
                Data complete: <strong>{formatCompleteDate(dataCompleteAt)}</strong>
              </div>
            )
          )}
        </div>
      )}

      <div className="sidebar-view-mode">
        <div className="sidebar-view-mode-label small text-muted">View mode</div>
        <Form.Check
          type="switch"
          id="sidebar-view-mode-switch"
          label={viewMode === 'advanced' ? 'Advanced' : 'Basic'}
          checked={viewMode === 'advanced'}
          onChange={(e) => setViewMode(e.target.checked ? 'advanced' : 'basic')}
        />
      </div>
    </div>
  );
}

export default Sidebar;
